import { useCallback } from "react";

import { IconPlus, IconRefresh } from "@tabler/icons";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useTheme } from "styled-components";

import { useGetSoftwareLicencesQuery } from "@sso/apis/softwareLicences/hooks";
import { TableHeader } from "@sso/components/TableHeader";
import { TableLayoutWrapper } from "@sso/components/TableLayoutWrapper";
import { TablePagination } from "@sso/components/TablePagination";
import { Dialog } from "@sso/constant";
import { TableProvider, TableProviderState } from "@sso/context/tableContext";
import { useAppDispatch, useAppSelector } from "@sso/hooks/useRedux";
import { openDialog } from "@sso/store/slices/dialog";
import { selectSoftwareLicencePagination } from "@sso/store/slices/softwareLicence";
import { Button } from "@sso/ui/Button";
import { CircleLoading } from "@sso/ui/CircleLoading";
import { Flex } from "@sso/ui/FlexBox";
import { IconButton } from "@sso/ui/IconButton";

import LicenceTypesTable from "./licenceTypesTable";
import { licenceTypeTableColumns } from "./licenceTypeTableColumns";

const LicenceTypes = () => {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const pagination = useAppSelector(selectSoftwareLicencePagination);
  const navigate = useNavigate();
  const { data, isFetching, error, refetch } = useGetSoftwareLicencesQuery();

  const { softwareName } = useParams();

  const tableContextInstance: TableProviderState = { ...pagination, columns: licenceTypeTableColumns };

  const handleAddSoftwareLicence = useCallback(
    () => dispatch(openDialog({ type: Dialog.ADD_LICENCE_TYPE })),
    [dispatch],
  );

  const handleBackToProducts = () => {
    navigate("/management/products-licences");
  };

  const TableActions = (
    <Flex>
      <IconButton title="Refresh" onClick={refetch}>
        <IconRefresh />
      </IconButton>
      <IconButton title="Add Licence Type" onClick={handleAddSoftwareLicence}>
        <IconPlus />
      </IconButton>
    </Flex>
  );

  const FooterActions = (
    <Button onClick={() => handleBackToProducts()} fontSize="1rem">
      Back to products
    </Button>
  );

  return (
    <>
      {isFetching && <CircleLoading color={theme.colors.secondary.dark} size={8} blurBackground />}
      <TableProvider tableInstance={tableContextInstance}>
        <TableHeader title={`${softwareName} Licence Types`} TableActions={TableActions} />
        <TableLayoutWrapper error={error} data={data}>
          {data && <LicenceTypesTable data={data.resources} tableColumns={licenceTypeTableColumns} />}
        </TableLayoutWrapper>
        <TablePagination actionButtons={FooterActions} totalPages={data?.totalPages} />
      </TableProvider>
    </>
  );
};

export default LicenceTypes;
